import { useEffect, useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useAuth } from './contexts/AuthContext';
import { useBrand } from './contexts/BrandContext';
import { isOnboardingComplete } from '@traaker/shared';
import { getLoginUrl } from './services/auth';
import DashboardLayout from './components/layout/DashboardLayout';
import ApiDocs from './pages/ApiDocs';
import AuthCallback from './pages/AuthCallback';
import Onboarding from './pages/Onboarding';
import Signup from './pages/Signup';
import CheckoutLaunch from './pages/CheckoutLaunch';
import BrandOnboarding from './pages/BrandOnboarding';
import Dashboard from './pages/Dashboard';
import Settings from './pages/Settings';
import Audits from './pages/Audits';
import AuditDetail from './pages/AuditDetail';
import BrandPerception from './pages/BrandPerception';
import BrandAxisPage from './pages/BrandAxisPage';
import BrandStrategy from './pages/BrandStrategy';
import GeoStrategy from './pages/GeoStrategy';
import Competitors from './pages/Competitors';
import EarnedMedia from './pages/EarnedMedia';
import NewsWatch from './pages/NewsWatch';
import ArticleEditor from './pages/news/ArticleEditor';
import Tasks from './pages/Tasks';
import CreditLedger from './pages/CreditLedger';
import AuditReport from './pages/reports/AuditReport';
import ReportLibrary from './pages/reports/ReportLibrary';
import ProductCatalog from './pages/product/ProductCatalog';
import ProductOptimization from './pages/product/ProductOptimization';
import ProductMeasurement from './pages/product/ProductMeasurement';
import ProductContent from './pages/product/ProductContent';
import AdminTenants from './pages/admin/AdminTenants';
import AdminTenantCreate from './pages/admin/AdminTenantCreate';
import AdminTenantDetail from './pages/admin/AdminTenantDetail';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { loading, user, needsOnboarding } = useAuth();
  const unauthenticated = !loading && !user && !needsOnboarding;

  useEffect(() => {
    if (unauthenticated) {
      window.location.replace(getLoginUrl());
    }
  }, [unauthenticated]);

  if (loading || unauthenticated) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (needsOnboarding) return <Navigate to="/onboarding" replace />;

  return <>{children}</>;
}

// Redirects a tenant with zero brands into the brand-onboarding wizard. The
// wizard route itself lives outside DashboardLayout, so this gate only guards
// the in-app surfaces. Agency tenants are NOT exempted today — a zero-brand
// agency still needs a first brand before the app is useful.
function OnboardingGate({ children }: { children: React.ReactNode }) {
  const { brands, loading } = useBrand();

  // Wait for the brand list to resolve before deciding — avoids a redirect
  // flash on first paint while BrandContext is still fetching.
  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (brands.length === 0) {
    return <Navigate to="/onboarding/brand/new" replace />;
  }

  // A brand row exists from wizard step 1, long before onboarding finishes — so
  // "a brand exists" is NOT "onboarding done". If the tenant has brands but NONE
  // completed onboarding, the first brand was abandoned mid-wizard (strategy
  // never validated, prompts/audits never created). Route back in to RESUME it
  // rather than dropping the user into a half-configured app. Once at least one
  // brand is fully onboarded, the app is usable and we don't trap the user (a
  // second abandoned brand is handled in-app, not by the global gate).
  if (!brands.some(isOnboardingComplete)) {
    const resumeId = brands.find((b) => !isOnboardingComplete(b))?.brandId;
    if (resumeId) return <Navigate to={`/onboarding/brand/${resumeId}`} replace />;
  }

  return <>{children}</>;
}

// Forces an admin-provisioned, pay-first (`pending`) tenant through Stripe
// checkout before the app unlocks — the "ordering" flow (email → login →
// checkout → first brand). Scoped to `pending` ONLY: trialing (basic time-trial
// / agency credit-trial), active, past_due, etc. all pass through untouched, so
// no existing flow changes. Runs after ProtectedRoute so `tenant` is loaded.
function BillingGate({ children }: { children: React.ReactNode }) {
  const { tenant, refreshProfile } = useAuth();
  const isPendingStripe = tenant?.billingStatus === 'pending' && tenant?.billingMode === 'stripe';
  const [verifying, setVerifying] = useState(isPendingStripe);

  // A cached tenant snapshot can lag the webhook: after payment Stripe returns to
  // /onboarding/brand/new, but if the checkout.session.completed webhook hasn't
  // flipped billingStatus to 'active' yet (~1-2s), the snapshot stays 'pending'
  // for the whole onboarding session. Re-fetch the profile ONCE before deciding,
  // so a freshly-paid tenant isn't bounced into a duplicate checkout at '/'.
  useEffect(() => {
    if (!isPendingStripe) {
      setVerifying(false);
      return;
    }
    let alive = true;
    refreshProfile().finally(() => {
      if (alive) setVerifying(false);
    });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (verifying) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Only a stripe-billed pending tenant is force-checked. A pending tenant with
  // no Stripe billing mode (shouldn't happen post-provisioning, but be safe)
  // falls through rather than looping into a checkout it can't complete.
  if (tenant?.billingStatus === 'pending' && tenant?.billingMode === 'stripe') {
    return <Navigate to="/checkout/launch" replace />;
  }
  return <>{children}</>;
}

function PlatformAdminRoute({ children }: { children: React.ReactNode }) {
  const { loading, user } = useAuth();
  if (loading) return null;
  if (!user?.isPlatformAdmin) return <Navigate to="/" replace />;
  return <>{children}</>;
}

export default function App() {
  return (
    <Routes>
      <Route path="/auth/callback" element={<AuthCallback />} />
      {/* Public REST API reference. OUTSIDE ProtectedRoute on purpose — a
          developer evaluating the API has no account yet, and the OpenAPI spec
          it renders contains no customer data. */}
      <Route path="/api-docs" element={<ApiDocs />} />
      <Route path="/signup" element={import.meta.env.PROD ? <Signup /> : <Navigate to="/" replace />} />
      <Route path="/onboarding" element={<Onboarding />} />
      <Route
        path="/checkout/launch"
        element={
          <ProtectedRoute>
            <CheckoutLaunch />
          </ProtectedRoute>
        }
      />
      <Route
        path="/onboarding/brand/:brandId"
        element={
          <ProtectedRoute>
            <BrandOnboarding />
          </ProtectedRoute>
        }
      />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <BillingGate>
              <OnboardingGate>
                <DashboardLayout />
              </OnboardingGate>
            </BillingGate>
          </ProtectedRoute>
        }
      >
        <Route index element={<Dashboard />} />
        <Route path="strategy" element={<BrandStrategy />} />
        <Route path="geo-strategy" element={<GeoStrategy />} />
        <Route path="audits" element={<Audits />} />
        <Route path="audits/:auditId" element={<AuditDetail />} />
        <Route path="earned-media" element={<EarnedMedia />} />
        <Route path="brand" element={<BrandPerception />} />
        <Route path="brand/narrative" element={<BrandAxisPage axis="narrative" />} />
        <Route path="brand/authority" element={<BrandAxisPage axis="authority" />} />
        <Route path="brand/referral" element={<BrandAxisPage axis="referral" />} />
        <Route path="social" element={<div>Social Audit — Phase 5</div>} />
        <Route path="tasks" element={<Tasks />} />
        <Route path="news-watch" element={<NewsWatch />} />
        <Route path="news-watch/article/:articleId" element={<ArticleEditor />} />
        <Route path="competitors" element={<Competitors />} />
        <Route path="product/catalog" element={<ProductCatalog />} />
        <Route path="product/optimization" element={<ProductOptimization />} />
        <Route path="product/measurement" element={<ProductMeasurement />} />
        <Route path="product/content" element={<ProductContent />} />
        <Route path="reports/audit" element={<AuditReport />} />
        <Route path="reports/library" element={<ReportLibrary />} />
        <Route path="settings" element={<Settings />} />
        <Route path="credits/ledger" element={<CreditLedger />} />
        <Route
          path="admin/tenants"
          element={<PlatformAdminRoute><AdminTenants /></PlatformAdminRoute>}
        />
        <Route
          path="admin/tenants/new"
          element={<PlatformAdminRoute><AdminTenantCreate /></PlatformAdminRoute>}
        />
        <Route
          path="admin/tenants/:tenantId"
          element={<PlatformAdminRoute><AdminTenantDetail /></PlatformAdminRoute>}
        />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
